import { IStorageTradingKey, MainWallet } from '../interfaces';
import { Chain } from '../redux/_reducers/wallet.reducer';
import secureLocalStorage from 'react-secure-storage';

const host = new URL((window.location !== window.parent.location) ? document.referrer : document.location.href).host;
const addHostPostfix = (key) => `${key}_${host}`;

const secureLocalStorageProxy = new Proxy(secureLocalStorage, {
  get(target, prop, receiver) {
    if (typeof prop === 'string' && typeof target[prop] === 'function') {
      return function(...args) {
        if (typeof prop === 'string' && ['setItem', 'getItem', 'removeItem'].includes(prop)) {
          args[0] = addHostPostfix(args[0]);
        }
        return target[prop].apply(target, args);
      };
    }
    return Reflect.get(target, prop, receiver);
  }
});

export const localStorageProxy = new Proxy(localStorage, {
  get(target, prop, receiver) {
    if (typeof prop === 'string' && typeof target[prop] === 'function') {
      return function(...args) {
        if (typeof prop === 'string' && ['setItem', 'getItem', 'removeItem', 'key'].includes(prop)) {
          args[0] = addHostPostfix(args[0]);
        }
        return target[prop].apply(target, args);
      };
    }
    return Reflect.get(target, prop, receiver);
  }
});

const hostname = new URL(
  window.location !== window.parent.location
    ? document.referrer
    : document.location.href
).host;

const getKeyWithHostname = (key: string) => key + '_' + hostname;

const getKeyWithLoginName = () => {
  const data = loginTokenService.getMainWallet();
  if (data) {
    return `${keys.tradingKey}-${data.address}`
  }
  return ""
};

export const keys = {
  accessCode: getKeyWithHostname('X-Ultrade-Access-Code'),
  currentTheme: getKeyWithHostname('current_theme'),
  version: getKeyWithHostname('version'),
  useWallet: 'txnlab-use-wallet',
  chains: 'chains',
  mainWallet: 'main-wallet',
  tradingKey: 'trading-key',
  referralToken: 'referralToken',
  appUrl: 'appUrl'
} as const;

export const getAccessCodeFromLocalStorage = () =>
  localStorageProxy.getItem(keys.accessCode);

export const setAccessCodeToLocalStorage = (code: string) =>
  localStorageProxy.setItem(keys.accessCode, code);

export const setCurrentThemeToLocalStorage = (id: string) =>
  localStorageProxy.setItem(keys.currentTheme, id);

export const getCurrentThemeFromLocalStorage = () =>
  localStorageProxy.getItem(keys.currentTheme);

export const getVersionFromLocalStorage = () =>
  localStorageProxy.getItem(keys.version);

export const setVersionToLocalStorage = (version: string) =>
  localStorageProxy.setItem(keys.version, version);

export const setUseWalletState = (setUseWalletState: string) =>
  localStorageProxy.setItem(keys.useWallet, setUseWalletState);

export const clearUseWalletState = () =>
  localStorageProxy.removeItem(keys.useWallet);

export const getChains = (): Chain[] => {
  const chainsStr = localStorageProxy.getItem(keys.chains);
  return chainsStr ? JSON.parse(chainsStr) : [];
};

export const setChains = (chains: Chain[]) =>
  localStorageProxy.setItem(keys.chains, JSON.stringify(chains));

export const getReferralToken = (): string => 
  localStorageProxy.getItem(keys.referralToken);

export const setReferralToken = (token: string) =>
  localStorageProxy.setItem(keys.referralToken, token);

export const clearReferralToken = () =>
  localStorageProxy.removeItem(keys.referralToken);

export const setAppUrl = (url: string) =>
  localStorageProxy.setItem(keys.appUrl, url);

export const getAppUrl = (): string | null => 
  localStorageProxy.getItem(keys.appUrl);

export const secureStorage = {
  getKeyFromLocalStorage (): IStorageTradingKey | null {
    const key = secureLocalStorageProxy.getItem(getKeyWithLoginName()) as IStorageTradingKey;
    return key ? key : null;
  },
  setKeyToLocalStorage (keyData: IStorageTradingKey) {
    secureLocalStorageProxy.setItem(getKeyWithLoginName(), keyData)
  },
  clearKeyState() {
    secureLocalStorageProxy.removeItem(getKeyWithLoginName())
  }
}

export const loginTokenService = {
  setMainWallet(mainWallet: MainWallet) {
    localStorageProxy.setItem(keys.mainWallet, JSON.stringify(mainWallet));
  },
  getMainWallet(): MainWallet | null {
    const mainWallet = localStorageProxy.getItem(keys.mainWallet);
    return mainWallet ? JSON.parse(mainWallet) : null;
  },
  clearMainWallet() {
    localStorageProxy.removeItem(keys.mainWallet);
  },
};
